import { Link } from "react-router-dom";
import { RefreshCcw } from "lucide-react";
import VendorRow from "./VendorRow";
import Pagination from "./Pagination";
import AdminSearchBar from "./AdminSearchBar";

const VendorFormDataPanel = ({
  vendorSubTab,
  setVendorSubTab,
  counts,
  vendorsForTab,
  loadingV,
  reloadVendors,
  onStatus,
  onView,
  onCycleReview,
  onMarkReviewComplete,
  onDelete,
  pagination,
  query,
  onSearch,
}) => (
  <div data-testid="section-vendor-form-data">
    <span className="font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
      Submissions
    </span>
    <h2 className="font-serif-display mt-3 text-3xl sm:text-4xl text-[var(--ld-ink)] font-medium leading-tight">
      Vendor Form data
    </h2>

    <div className="mt-6">
      <AdminSearchBar
        placeholder="Search by company, name, email, location…"
        initialValue={query || ""}
        onSearch={onSearch}
      />
    </div>

    {/* Sub-tabs: Pending / Approved / Rejected */}
    <div className="mt-8 flex items-center gap-1 border-b border-[var(--ld-border)]">
      {[
        { id: "pending", label: "Pending", count: counts?.pending ?? 0 },
        { id: "approved", label: "Approved", count: counts?.approved ?? 0 },
        { id: "rejected", label: "Rejected", count: counts?.rejected ?? 0 },
      ].map((t) => (
        <button
          key={t.id}
          type="button"
          data-testid={`vendor-subtab-${t.id}`}
          onClick={() => setVendorSubTab(t.id)}
          className={
            "px-5 py-3 text-[11px] tracking-[0.2em] uppercase border-b-2 -mb-px transition-colors " +
            (vendorSubTab === t.id
              ? "border-[var(--ld-gold-deep)] text-[var(--ld-ink)]"
              : "border-transparent text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]")
          }
        >
          {t.label}
          <span className="ml-2 text-[10px] text-[var(--ld-ink-muted)]">
            {t.count}
          </span>
        </button>
      ))}
    </div>

    <div className="flex items-center justify-between mt-6 mb-3">
      <p className="text-xs text-[var(--ld-ink-muted)]">
        {vendorSubTab === "rejected" ? (
          <>
            Rejected applications are kept here. Use{" "}
            <strong>Restore</strong> to move them back to pending.
          </>
        ) : vendorSubTab === "approved" ? (
          <>
            Approved vendors are surfaced on{" "}
            <Link
              to="/vendor-listing"
              className="underline underline-offset-4 hover:text-[var(--ld-ink)]"
            >
              /vendor-listing
            </Link>
            . Click the review pill to cycle: auto → green → red → auto.
          </>
        ) : (
          <>
            New applications land here. Approve to publish on{" "}
            <Link
              to="/vendor-listing"
              className="underline underline-offset-4 hover:text-[var(--ld-ink)]"
            >
              /vendor-listing
            </Link>
            , or reject to archive.
          </>
        )}
      </p>
      <button
        onClick={reloadVendors}
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)]"
      >
        <RefreshCcw size={12} strokeWidth={1.6} />
        Refresh
      </button>
    </div>
    <div className="border border-[var(--ld-border)] overflow-x-auto">
      <table className="w-full text-left">
        <thead className="bg-[var(--ld-ivory-warm)]/50">
          <tr className="text-[10px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)]">
            <th className="py-3 px-4">Company</th>
            <th className="py-3 px-4">Services</th>
            <th className="py-3 px-4">Region</th>
            <th className="py-3 px-4">Accepted date</th>
            <th className="py-3 px-4">Review</th>
            <th className="py-3 px-4">Last sign-in</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loadingV && (
            <tr>
              <td colSpan={8} className="py-8 text-center text-sm text-[var(--ld-ink-muted)]">
                Loading…
              </td>
            </tr>
          )}
          {!loadingV && vendorsForTab.length === 0 && (
            <tr>
              <td colSpan={8} className="py-10 text-center text-sm text-[var(--ld-ink-muted)]">
                {vendorSubTab === "rejected"
                  ? "No rejected applications."
                  : vendorSubTab === "approved"
                  ? "No approved vendors yet."
                  : "No pending applications."}
              </td>
            </tr>
          )}
          {vendorsForTab.map((app) => (
            <VendorRow
              key={app.id}
              app={app}
              mode={vendorSubTab}
              onStatus={onStatus}
              onView={onView}
              onCycleReview={onCycleReview}
              onMarkReviewComplete={onMarkReviewComplete}
              onDelete={onDelete}
            />
          ))}
        </tbody>
      </table>
    </div>
    {pagination && (
      <Pagination
        page={pagination.page}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onPageChange={pagination.onPageChange}
        loading={pagination.loading}
      />
    )}
  </div>
);

export default VendorFormDataPanel;
